import { homedir } from "node:os";
import { join } from "node:path";
import { exists, readFile, writeFile } from "./fsx.js";

/**
 * Every environment key the storefront template consumes, and how it is delivered.
 *
 * This is the single source of truth. `template-env-coverage.test.js` greps the
 * template for `process.env.X` and fails when a key is missing here, because this
 * exact gap has now shipped three times: CARRIER_API_KEY (site down),
 * STRIPE_SECRET_KEY and NEXT_PUBLIC_APP_URL (guest checkout silently degraded).
 *
 *   "secret" — server-side only. Must be pushed to the deploy target.
 *   "public" — NEXT_PUBLIC_*, inlined by Next at build time. Must be in
 *              .env.local *before* the build, and pushing it post-deploy is useless.
 *
 * Clerk's keys are listed even though the template never names them in
 * `process.env` — @clerk/nextjs reads them from the environment itself.
 */
export const TEMPLATE_ENV_KEYS = {
  NEXT_PUBLIC_BRAND_NAME: "public",
  NEXT_PUBLIC_CARRIER_API_URL: "public",
  NEXT_PUBLIC_APP_URL: "public",
  NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY: "public",
  // Optional. Present: checkout is embedded in the storefront's own page.
  // Absent: checkout redirects to Stripe's hosted page, which still works.
  NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: "public",
  CARRIER_API_KEY: "secret",
  CLERK_SECRET_KEY: "secret",
  STRIPE_SECRET_KEY: "secret",
  // Optional. Present: the storefront emails the QR code to whoever paid.
  // Absent: the activation page says email delivery is not set up, and every
  // install route it shows on screen still works.
  RESEND_API_KEY: "secret",
} as const;

export type TemplateEnvKey = keyof typeof TEMPLATE_ENV_KEYS;

function keysOfKind(kind: "secret" | "public"): TemplateEnvKey[] {
  return (Object.keys(TEMPLATE_ENV_KEYS) as TemplateEnvKey[]).filter(
    (k) => TEMPLATE_ENV_KEYS[k] === kind,
  );
}

export const RUNTIME_SECRET_KEYS: readonly TemplateEnvKey[] = keysOfKind("secret");

/** Written into .env.local and inlined at build time. */
export const PUBLIC_ENV_KEYS: readonly TemplateEnvKey[] = keysOfKind("public");

export type SecretMap = Record<string, string>;

/** Parse a dotenv file. Ignores comments; strips surrounding quotes. */
export function parseEnvFile(body: string): SecretMap {
  const out: SecretMap = {};
  for (const raw of body.split("\n")) {
    const line = raw.trim();
    if (!line || line.startsWith("#")) continue;
    const eq = line.indexOf("=");
    if (eq <= 0) continue;
    const key = line.slice(0, eq).trim();
    let value = line.slice(eq + 1).trim();
    if (
      (value.startsWith('"') && value.endsWith('"') && value.length > 1) ||
      (value.startsWith("'") && value.endsWith("'") && value.length > 1)
    ) {
      value = value.slice(1, -1);
    }
    if (value) out[key] = value;
  }
  return out;
}

async function readEnvFile(path: string): Promise<SecretMap> {
  if (!(await exists(path))) return {};
  try {
    return parseEnvFile(await readFile(path, "utf8"));
  } catch {
    return {};
  }
}

/**
 * Collect candidate values for one key, nearest source first.
 *
 * The storefront's own `.env.local` wins because it is the most specific, then
 * the live process env, then the operator's `~/.env`. Values are never logged.
 */
export async function discoverSecrets(
  storefront: string,
  env: Record<string, string | undefined> = process.env,
  home: string = homedir(),
): Promise<SecretMap> {
  const local = await readEnvFile(join(storefront, ".env.local"));
  const global = await readEnvFile(join(home, ".env"));

  const merged: SecretMap = {};
  const keys = [...RUNTIME_SECRET_KEYS, ...PUBLIC_ENV_KEYS];
  for (const key of keys) {
    const value = local[key]?.trim() || env[key]?.trim() || global[key]?.trim();
    if (value) merged[key] = value;
  }
  return merged;
}

/**
 * Merge values into a storefront `.env.local`, preserving comments and order.
 *
 * An existing non-empty value is only replaced when `overwrite` names that key —
 * re-running a deploy must not clobber keys the operator edited by hand.
 */
export async function mergeEnvLocal(
  storefront: string,
  updates: SecretMap,
  opts: { overwrite?: string[] } = {},
): Promise<string[]> {
  const path = join(storefront, ".env.local");
  const overwrite = new Set(opts.overwrite ?? []);
  const original = (await exists(path)) ? await readFile(path, "utf8") : "";
  const lines = original ? original.split("\n") : [];
  const written: string[] = [];

  for (const [key, value] of Object.entries(updates)) {
    if (!value) continue;
    const index = lines.findIndex((l) => l.trim().startsWith(`${key}=`));
    if (index === -1) {
      lines.push(`${key}=${value}`);
      written.push(key);
      continue;
    }
    const current = lines[index].slice(lines[index].indexOf("=") + 1).trim();
    const isEmpty = current === "" || current === '""' || current === "''";
    if (isEmpty || overwrite.has(key)) {
      lines[index] = `${key}=${value}`;
      written.push(key);
    }
  }

  if (written.length === 0) return [];
  const body = lines.join("\n").replace(/\n{3,}$/, "\n");
  await writeFile(path, body.endsWith("\n") ? body : `${body}\n`);
  return written;
}

/** True when the storefront points at a live Carrier origin and so needs a key. */
export function needsCarrierKey(secrets: SecretMap): boolean {
  const url = secrets.NEXT_PUBLIC_CARRIER_API_URL?.trim();
  return Boolean(url) && !secrets.CARRIER_API_KEY?.trim();
}
